import { useState, useCallback, useEffect } from 'react'
import { Eye, EyeOff, CheckCircle2, AlertTriangle, Loader2, Play, RefreshCw, FlaskConical, BellRing } from 'lucide-react'
import { eventoSyncApi } from '@/services/api/eventoSync'
import type { EventoSettings, SyncType, DryRunResult } from '@/services/api/eventoSync'

interface EventoConfigSectionProps {
  settings: EventoSettings
  onUpdate: (payload: Partial<EventoSettings>) => Promise<void>
  onSyncTriggered: () => void
  onSyncCompleted: () => void
  webhookBaseUrl?: string
}

export type ConnectionHealth = 'checking' | 'connected' | 'disconnected' | 'unknown'

export function EventoConfigSection({ settings, onUpdate, onSyncTriggered, onSyncCompleted, webhookBaseUrl }: EventoConfigSectionProps) {
  const [form, setForm] = useState<Partial<EventoSettings>>(() => {
    try {
      const saved = localStorage.getItem('evento_config_draft')
      if (saved) return JSON.parse(saved)
    } catch {}
    return {}
  })
  const [showEventoKey, setShowEventoKey] = useState(false)
  const [showWebhookSecret, setShowWebhookSecret] = useState(false)
  const [testing, setTesting] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [connectionHealth, setConnectionHealth] = useState<ConnectionHealth>('unknown')
  const [lastConnectionTest, setLastConnectionTest] = useState<string | null>(null)
  const [syncType, setSyncType] = useState<SyncType>('full')
  const [dryRunning, setDryRunning] = useState(false)
  const [dryRunResult, setDryRunResult] = useState<DryRunResult | null>(null)
  const [webhookTesting, setWebhookTesting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const val = <T extends keyof EventoSettings>(key: T): EventoSettings[T] => {
    return form[key] !== undefined ? form[key] as EventoSettings[T] : settings[key]
  }

  const [scheduledSyncEnabled, setScheduledSyncEnabled] = useState<boolean>(() => val('evento_sync_enabled') ?? false)

  const apiUrl = val('evento_api_url')
  const apiKey = val('evento_api_key')

  const update = (key: keyof EventoSettings, value: unknown) => {
    setForm(f => {
      const updated = { ...f, [key]: value }
      try {
        localStorage.setItem('evento_config_draft', JSON.stringify(updated))
      } catch {}
      return updated
    })
  }

  const validateUrl = (url: string): boolean => {
    if (!url) return true
    try {
      const parsed = new URL(url)
      return parsed.protocol === 'https:'
    } catch {
      return false
    }
  }

  const validateKey = (key: string): boolean => {
    return !key || key.length >= 10
  }

  const isFormValid = validateUrl(apiUrl || '') && validateKey(apiKey || '')

  const handleTestConnection = useCallback(async () => {
    setTesting(true)
    setConnectionHealth('checking')
    setError(null)
    try {
      const result = await eventoSyncApi.testConnection(apiUrl || undefined, apiKey || undefined)
      if (result.success) {
        setConnectionHealth('connected')
        setLastConnectionTest(new Date().toISOString())
        setSuccess('Verbindung erfolgreich')
      } else {
        setConnectionHealth('disconnected')
        setError(result.message || 'Verbindung fehlgeschlagen')
      }
    } catch {
      setConnectionHealth('disconnected')
      setError('Verbindung fehlgeschlagen')
    } finally {
      setTesting(false)
    }
  }, [apiUrl, apiKey])

  const handleSave = useCallback(async () => {
    if (!isFormValid) {
      setError('Form validation failed')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([_, v]) => v !== undefined)
      )
      await onUpdate(payload)
      setForm({})
      try {
        localStorage.removeItem('evento_config_draft')
      } catch {}
      setSuccess('Einstellungen gespeichert')
    } catch (err) {
      setError('Fehler beim Speichern')
    } finally {
      setSaving(false)
    }
  }, [isFormValid, form, onUpdate])

  const handleSaveAndTest = useCallback(async () => {
    await handleSave()
    if (isFormValid) {
      await handleTestConnection()
    }
  }, [handleSave, handleTestConnection, isFormValid])

  const handleSync = useCallback(async () => {
    if (syncing) return // Debounce - prevent concurrent syncs
    setSyncing(true)
    setError(null)
    onSyncTriggered()
    try {
      const result = await eventoSyncApi.triggerSync(syncType)
      if (result.success) {
        setSuccess(`Sync gestartet (${syncType}): ${result.status}`)
      } else {
        setError('Sync fehlgeschlagen')
      }
      onSyncCompleted()
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Sync fehlgeschlagen'
      setError(errorMsg)
      onSyncCompleted()
    } finally {
      setSyncing(false)
    }
  }, [syncType, onSyncTriggered, onSyncCompleted, syncing])

  const handleDryRun = useCallback(async () => {
    if (dryRunning) return
    setDryRunning(true)
    setError(null)
    try {
      const result = await eventoSyncApi.dryRunSync(syncType)
      setDryRunResult(result)
      if (result.success) {
        setSuccess(`Dry-Run: Would create ${result.would_create}, update ${result.would_update}, skip ${result.would_skip}`)
      } else {
        setError('Dry-Run fehlgeschlagen')
      }
    } catch (err: unknown) {
      setError('Dry-Run fehlgeschlagen')
    } finally {
      setDryRunning(false)
    }
  }, [syncType, dryRunning])

  const handleWebhookTest = useCallback(async () => {
    if (webhookTesting) return
    setWebhookTesting(true)
    setError(null)
    try {
      const result = await eventoSyncApi.testWebhook()
      if (result.success) {
        setSuccess('Webhook Test erfolgreich')
      } else {
        setError(result.message || 'Webhook Test fehlgeschlagen')
      }
    } catch {
      setError('Webhook Test fehlgeschlagen')
    } finally {
      setWebhookTesting(false)
    }
  }, [webhookTesting])

  const handleScheduledSyncToggle = useCallback(async (enabled: boolean) => {
    setScheduledSyncEnabled(enabled)
    update('evento_sync_enabled', enabled)
    try {
      const result = await eventoSyncApi.toggleScheduledSync(enabled)
      if (result.success) {
        setSuccess(result.message)
      }
    } catch {
      setError('Toggle fehlgeschlagen')
    }
  }, [update])

  useEffect(() => {
    if (settings.evento_sync_enabled && settings.evento_api_url && settings.evento_api_key) {
      const testOnLoad = async () => {
        setTesting(true)
        setConnectionHealth('checking')
        try {
          const result = await eventoSyncApi.testConnection(settings.evento_api_url || undefined, settings.evento_api_key || undefined)
          if (result.success) {
            setConnectionHealth('connected')
          } else {
            setConnectionHealth('disconnected')
          }
        } catch {
          setConnectionHealth('disconnected')
        } finally {
          setTesting(false)
        }
      }
      testOnLoad()
    }
  }, [settings.evento_sync_enabled, settings.evento_api_url, settings.evento_api_key])

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [success])

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [error])

const generateWebhookSecret = useCallback(() => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    let secret = ''
    for (let i = 0; i < 32; i++) {
      secret += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    update('evento_webhook_secret', secret)
  }, [])

  const webhookUrl = webhookBaseUrl || window.location.origin

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-3 bg-red-100 text-red-700 rounded flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-500">&times;</button>
        </div>
      )}
      {success && (
        <div className="p-3 bg-green-100 text-green-700 rounded flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" />
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="ml-auto text-green-500">&times;</button>
        </div>
      )}

      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Evento API Konfiguration</h3>
          <div className="flex items-center gap-2">
            {connectionHealth === 'connected' && (
              <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Connected
              </span>
            )}
            {connectionHealth === 'connected' && lastConnectionTest && (
              <span className="text-xs text-gray-500">Last test: {new Date(lastConnectionTest).toLocaleTimeString('de-CH')}</span>
            )}
            {connectionHealth === 'disconnected' && (
              <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">Not Connected</span>
            )}
            {connectionHealth === 'checking' && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs animate-pulse">Checking...</span>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <label className="flex items-center gap-3">
            <input type="checkbox" checked={val('evento_sync_enabled') ?? false} onChange={e => update('evento_sync_enabled', e.target.checked)} className="w-4 h-4" />
            <span className="text-sm font-medium">Evento Sync aktiviert</span>
          </label>

          <div className={!val('evento_sync_enabled') ? 'opacity-50 pointer-events-none space-y-4' : 'space-y-4'}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">API URL</label>
              <input type="url" value={val('evento_api_url') ?? ''} onChange={e => update('evento_api_url', e.target.value || null)} placeholder="https://evento.ch/api/v1" className="w-full px-3 py-2 border rounded-lg text-sm" />
              {!validateUrl(val('evento_api_url') || '') && val('evento_api_url') && <p className="text-xs text-red-600 mt-1">URL must be valid HTTPS</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
              <div className="relative">
                <input type={showEventoKey ? 'text' : 'password'} value={val('evento_api_key') ?? ''} onChange={e => update('evento_api_key', e.target.value || null)} placeholder="••••••••" className="w-full px-3 py-2 border rounded-lg pr-10 text-sm" />
                <button type="button" onClick={() => setShowEventoKey(!showEventoKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showEventoKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {!validateKey(val('evento_api_key') || '') && val('evento_api_key') && <p className="text-xs text-red-600 mt-1">Key must be at least 10 characters</p>}
            </div>

            <div className="pt-2 border-t">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Sync-Typen</h4>
              <div className="space-y-2">
                <label className="flex items-center gap-3">
                  <input type="checkbox" checked={val('evento_sync_staff_enabled') ?? true} onChange={e => update('evento_sync_staff_enabled', e.target.checked)} className="w-4 h-4" />
                  <span className="text-sm">Lehrer synchronisieren</span>
                </label>
                <label className="flex items-center gap-3">
                  <input type="checkbox" checked={val('evento_sync_timetable_enabled') ?? true} onChange={e => update('evento_sync_timetable_enabled', e.target.checked)} className="w-4 h-4" />
                  <span className="text-sm">Stundenpläne synchronisieren</span>
                </label>
                <label className="flex items-center gap-3">
                  <input type="checkbox" checked={val('evento_sync_calendar_enabled') ?? true} onChange={e => update('evento_sync_calendar_enabled', e.target.checked)} className="w-4 h-4" />
                  <span className="text-sm">Ferien synchronisieren</span>
                </label>
                <label className="flex items-center gap-3">
                  <input type="checkbox" checked={val('evento_sync_substitutions_enabled') ?? false} onChange={e => update('evento_sync_substitutions_enabled', e.target.checked)} className="w-4 h-4" />
                  <span className="text-sm">Vertretungen synchronisieren (bidirektional)</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sync-Frequenz</label>
              <select value={val('evento_sync_frequency') ?? 'daily'} onChange={e => update('evento_sync_frequency', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
                <option value="hourly">Stündlich</option>
                <option value="daily">Täglich</option>
                <option value="weekly">Wöchentlich</option>
                <option value="monthly">Monatlich</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Konflikte-Strategie</label>
              <select value={val('evento_sync_strategy') ?? 'evento_wins'} onChange={e => update('evento_sync_strategy', e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
                <option value="evento_wins">Evento gewinnt</option>
                <option value="local_wins">Lokale Daten gewinnen</option>
                <option value="merge">Zusammenführen</option>
              </select>
            </div>

            <div className="flex gap-3 pt-2">
              <button onClick={handleSaveAndTest} disabled={saving || testing || !isFormValid} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm flex items-center gap-2">
                {(saving || testing) && <Loader2 className="w-4 h-4 animate-spin" />}
                Speichern & Testen
              </button>
              <button onClick={handleTestConnection} disabled={testing || !isFormValid} className="px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm flex items-center gap-2">
                {testing && <Loader2 className="w-4 h-4 animate-spin" />}
                Testen
              </button>
            </div>

            <div className="pt-2 border-t">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Scheduled Sync</h4>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={scheduledSyncEnabled}
                  onChange={e => handleScheduledSyncToggle(e.target.checked)}
                  className="w-4 h-4"
                />
                <span className="text-sm">Automatische Syncs aktivieren (nach Frequenz)</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">
                When enabled, syncs will run automatically based on frequency setting.
              </p>
            </div>

            <div className="pt-2 border-t">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Manueller Sync</h4>
              <div className="flex gap-3 items-center flex-wrap">
                <select value={syncType} onChange={e => setSyncType(e.target.value as SyncType)} className="px-3 py-2 border rounded-lg text-sm">
                  <option value="full">Full Sync</option>
                  <option value="staff_only">Nur Lehrer</option>
                  <option value="timetable_only">Nur Stundenplan</option>
                  <option value="calendar_only">Nur Ferien</option>
                </select>
                <button onClick={handleSync} disabled={syncing || dryRunning} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm flex items-center gap-2">
                  {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  Sync starten
                </button>
                <button onClick={handleDryRun} disabled={syncing || dryRunning} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 text-sm flex items-center gap-2">
                  {dryRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
                  Dry-Run
                </button>
              </div>

              {dryRunResult && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg text-xs">
                  <h5 className="font-semibold mb-2">Dry-Run Preview</h5>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                    <div>Create: <span className="font-medium text-green-600">{dryRunResult.would_create}</span></div>
                    <div>Update: <span className="font-medium text-blue-600">{dryRunResult.would_update}</span></div>
                    <div>Skip: <span className="font-medium text-gray-600">{dryRunResult.would_skip}</span></div>
                    <div>Delete: <span className="font-medium text-red-600">{dryRunResult.would_delete}</span></div>
                  </div>
                  {dryRunResult.conflicts.length > 0 && (
                    <div className="text-yellow-600">
                      <span className="font-medium">Conflicts:</span> {dryRunResult.conflicts.length}
                    </div>
                  )}
                  {dryRunResult.warnings.length > 0 && (
                    <ul className="text-gray-500 list-disc list-inside">
                      {dryRunResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  )}
                  <button onClick={() => setDryRunResult(null)} className="text-xs text-gray-400 hover:text-gray-600 mt-1">
                    × Close
                  </button>
                </div>
              )}
            </div>

            {val('evento_last_sync_at') && (
              <p className="text-xs text-gray-500">Letzter Sync: {new Date(val('evento_last_sync_at') as string).toLocaleString('de-CH')}</p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border p-6">
        <h3 className="font-semibold mb-4">Webhook Konfiguration</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Webhook URL</label>
            <div className="flex items-center gap-2">
              <code className="px-3 py-2 bg-gray-100 rounded text-sm flex-1 overflow-x-auto">
                {webhookUrl}/api/v1/evento/webhook/receive
              </code>
              <button onClick={() => navigator.clipboard.writeText(`${webhookUrl}/api/v1/evento/webhook/receive`)} className="px-2 py-2 border rounded hover:bg-gray-50">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Konfiguriere diesen URL in Evento für real-time Updates.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Webhook Secret</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input type={showWebhookSecret ? 'text' : 'password'} value={val('evento_webhook_secret') ?? ''} onChange={e => update('evento_webhook_secret', e.target.value || null)} placeholder="••••••••" className="w-full px-3 py-2 border rounded-lg pr-10 text-sm" />
                <button type="button" onClick={() => setShowWebhookSecret(!showWebhookSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showWebhookSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <button onClick={generateWebhookSecret} className="px-3 py-2 border rounded hover:bg-gray-50 text-sm">Generieren</button>
            </div>
            <button onClick={handleWebhookTest} disabled={webhookTesting} className="px-3 py-2 border rounded hover:bg-gray-50 text-sm flex items-center gap-1">
              {webhookTesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <BellRing className="w-3 h-3" />}
              Test Webhook
            </button>
            {val('evento_webhook_secret') && <p className="text-xs text-green-600 mt-1">Secret konfiguriert ✓</p>}
          </div>

          <button onClick={handleSave} disabled={saving || !isFormValid} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm">
            {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  )
}